import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./MenuVerification.css";
import api from "../../services/api";
import { useToast } from "../../mesageToast/ToastContainer";
import Veg from "../../Assets/veg.svg";
import NonVeg from "../../Assets/NonVeg.svg";
import cross from "../../Assets/cross.svg";

const createEmptyForm = () => ({
  dishName: "",
  category: "",
  addNewCategory: "",
  dishDescription: "",
  itemPrice: "",
  markup: "0",
  isVeg: true,
  images: [],
});

const createEmptyAddonForm = () => ({
  name: "",
  dishType: 0,
  itemPrice: "",
  markup: "0",
});

const toFormValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatDate = (date) => {
  if (!date) {
    return "N/A";
  }

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const buildFormFromDish = (dish) => ({
  dishName: dish?.dishName || "",
  category: dish?.dishCategory || "",
  addNewCategory: "",
  dishDescription: dish?.dishDescription || "",
  itemPrice: toFormValue(dish?.itemPrice),
  markup: toFormValue(dish?.markup ?? 0),
  isVeg: dish?.dishType !== 1,
  images:
    dish?.dishImageUrl?.map((url, index) => ({
      preview: url,
      key: dish?.dishImageKeys?.[index] || null,
      isExisting: true,
    })) || [],
});

const buildFormFromAddon = (addon) => ({
  name: addon?.name || "",
  dishType: addon?.dishType === 1 ? 1 : 0,
  itemPrice: toFormValue(addon?.itemPrice),
  markup: toFormValue(addon?.markup ?? 0),
});

const MenuVerification = ({ onBack, restaurantId }) => {
  const [activeTab, setActiveTab] = useState("pending");
  const [dishes, setDishes] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [form, setForm] = useState(createEmptyForm());
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("edit");
  const [selectedAddonDish, setSelectedAddonDish] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [addonForm, setAddonForm] = useState(createEmptyAddonForm());
  const [addonErrors, setAddonErrors] = useState({});
  const [isAddonModalOpen, setIsAddonModalOpen] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [addonSubmitInProgress, setAddonSubmitInProgress] = useState(false);
  const [rejectInProgress, setRejectInProgress] = useState(false);
  const fileInputRef = useRef(null);
  const addToast = useToast();

  const fetchDishes = async () => {
    if (!restaurantId) {
      setDishes([]);
      return;
    }

    setLoading(true);
    try {
      const endpoint =
        activeTab === "pending"
          ? "/restaurant-management/menu/pending-dishes"
          : "/restaurant-management/menu/verified-dishes";
      const response = await api.post(endpoint, { restaurantId });
      setDishes(response.data?.dishes || []);
    } catch (error) {
      console.error(`Error fetching ${activeTab} dishes:`, error);
      setDishes([]);
      addToast("Error", "Failed to load dishes.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDishes();
  }, [activeTab, restaurantId]);

  const fetchCategories = async () => {
    if (!restaurantId) {
      setCategoryList([]);
      return;
    }

    try {
      const response = await api.get("/restaurant-management/menu/restaurants", {
        headers: { "x-restaurant-id": restaurantId },
      });
      setCategoryList(response.data?.categories || []);
    } catch (error) {
      console.error("Error fetching category list:", error);
      setCategoryList([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [restaurantId]);

  const availableCategories = useMemo(() => {
    const categories = new Set(
      categoryList.filter(
        (category) => typeof category === "string" && category.trim()
      )
    );

    if (selectedDish?.dishCategory) {
      categories.add(selectedDish.dishCategory);
    }

    return Array.from(categories).sort((left, right) =>
      left.localeCompare(right)
    );
  }, [categoryList, selectedDish]);

  const totalPrice = useMemo(
    () => (toNumber(form.itemPrice) + toNumber(form.markup)).toFixed(2),
    [form.itemPrice, form.markup]
  );

  const addonTotalPrice = useMemo(
    () => (toNumber(addonForm.itemPrice) + toNumber(addonForm.markup)).toFixed(2),
    [addonForm.itemPrice, addonForm.markup]
  );

  const resetModalState = () => {
    setSelectedDish(null);
    setForm(createEmptyForm());
    setErrors({});
    setIsModalOpen(false);
    setModalAction("edit");
  };

  const resetAddonModalState = () => {
    setSelectedAddonDish(null);
    setSelectedAddon(null);
    setAddonForm(createEmptyAddonForm());
    setAddonErrors({});
    setIsAddonModalOpen(false);
  };

  const cleanupUnsavedImages = async () => {
    const removableKeys = form.images
      .filter((image) => image.key && !image.isExisting)
      .map((image) => image.key);

    form.images.forEach((image) => {
      if (image.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(image.preview);
      }
    });

    if (removableKeys.length > 0 && restaurantId) {
      try {
        await api.delete("/restaurant-management/removeDishImages", {
          headers: { "x-restaurant-id": restaurantId },
          data: { s3Keys: removableKeys },
        });
      } catch (error) {
        console.error("Error cleaning up uploaded images:", error);
      }
    }
  };

  const closeModal = async () => {
    await cleanupUnsavedImages();
    resetModalState();
  };

  const openDishModal = (dish, action = "edit") => {
    setSelectedDish(dish);
    setForm(buildFormFromDish(dish));
    setErrors({});
    setModalAction(action);
    setIsModalOpen(true);
  };

  const openAddonModal = (dish, addon) => {
    setSelectedAddonDish(dish);
    setSelectedAddon(addon);
    setAddonForm(buildFormFromAddon(addon));
    setAddonErrors({});
    setIsAddonModalOpen(true);
  };

  const updateFormValue = (field, value) => {
    setForm((currentForm) => ({ ...currentForm, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: "" }));
  };

  const updateAddonFormValue = (field, value) => {
    setAddonForm((currentForm) => ({ ...currentForm, [field]: value }));
    setAddonErrors((currentErrors) => ({ ...currentErrors, [field]: "" }));
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    const file = files[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      addToast("Error", "Please upload a valid image file.", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      addToast("Error", "Image must be 5MB or smaller.", "error");
      return;
    }

    try {
      const response = await api.get(
        "/restaurant-management/menu/generatePresignedUrl",
        {
          headers: { "x-restaurant-id": restaurantId },
          params: {
            fileType: file.type,
            documentType: "dishImage",
            dishId: selectedDish?._id,
          },
        }
      );

      const { uploadUrl, key } = response.data;

      await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      setForm((currentForm) => ({
        ...currentForm,
        images: [
          {
            preview: URL.createObjectURL(file),
            key,
            isExisting: false,
          },
        ],
      }));
      setErrors((currentErrors) => ({ ...currentErrors, images: "" }));
    } catch (error) {
      console.error("Error uploading image:", error);
      addToast("Error", "Failed to upload image.", "error");
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveImage = async (index) => {
    const imageToRemove = form.images[index];
    if (!imageToRemove) {
      return;
    }

    if (imageToRemove.preview?.startsWith("blob:")) {
      URL.revokeObjectURL(imageToRemove.preview);
    }

    if (imageToRemove.key && !imageToRemove.isExisting) {
      try {
        await api.delete("/restaurant-management/removeDishImages", {
          headers: { "x-restaurant-id": restaurantId },
          data: { s3Keys: [imageToRemove.key] },
        });
      } catch (error) {
        console.error("Error removing uploaded image:", error);
      }
    }

    setForm((currentForm) => ({
      ...currentForm,
      images: currentForm.images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const normalizedCategory =
      form.category === "Others" ? form.addNewCategory.trim() : form.category;

    if (!form.dishName.trim()) {
      nextErrors.dishName = "Dish name is required.";
    }

    if (!form.category) {
      nextErrors.category = "Category is required.";
    }

    if (form.category === "Others" && !form.addNewCategory.trim()) {
      nextErrors.addNewCategory = "New category is required.";
    }

    if (!normalizedCategory) {
      nextErrors.category = "Category is required.";
    }

    if (toNumber(form.itemPrice) <= 0) {
      nextErrors.itemPrice = "Item price must be greater than 0.";
    }

    if (toNumber(form.markup) < 0) {
      nextErrors.markup = "Markup cannot be negative.";
    }

    if (form.images.length === 0) {
      nextErrors.images = "Dish image is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateAddonForm = () => {
    const nextErrors = {};

    if (!addonForm.name.trim()) {
      nextErrors.name = "Add-on name is required.";
    }

    if (toNumber(addonForm.itemPrice) <= 0) {
      nextErrors.itemPrice = "Item price must be greater than 0.";
    }

    if (toNumber(addonForm.markup) < 0) {
      nextErrors.markup = "Markup cannot be negative.";
    }

    setAddonErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveDishDetails = async (shouldApprove = false) => {
    if (!selectedDish || !restaurantId || !validateForm()) {
      return;
    }

    setSubmitInProgress(true);
    const normalizedCategory =
      form.category === "Others" ? "Others" : form.category;
    const itemPrice = toNumber(form.itemPrice);
    const markup = toNumber(form.markup);

    try {
      await api.post(
        "/restaurant-management/menu/updateDetails",
        {
          dishName: form.dishName.trim(),
          category: normalizedCategory,
          addNewCategory: form.addNewCategory.trim(),
          price: itemPrice + markup,
          itemPrice,
          markup,
          isVeg: form.isVeg,
          images: form.images.map((image) => image.key).filter(Boolean),
          dishDescription: form.dishDescription.trim(),
          dishDetails: { _id: selectedDish._id },
        },
        {
          headers: { "x-restaurant-id": restaurantId },
        }
      );

      if (shouldApprove) {
        await api.post("/restaurant-management/menu/approve-dish", {
          dishId: selectedDish._id,
          markup,
        });
      }

      await fetchDishes();
      await fetchCategories();
      addToast(
        shouldApprove ? "Dish Approved" : "Dish Updated",
        shouldApprove
          ? "Dish has been approved successfully."
          : "Dish details have been updated.",
        "success"
      );
      resetModalState();
    } catch (error) {
      console.error("Error saving dish details:", error);
      addToast("Error", "Failed to save dish details.", "error");
    } finally {
      setSubmitInProgress(false);
    }
  };

  const handleRejectDish = async (dish) => {
    if (!window.confirm(`Are you sure you want to reject ${dish.dishName}?`)) {
      return;
    }

    setRejectInProgress(true);
    try {
      await api.post("/restaurant-management/menu/reject-dish", {
        dishId: dish._id,
      });
      await fetchDishes();
      addToast("Dish Rejected", "Dish has been rejected.", "success");
    } catch (error) {
      console.error("Error rejecting dish:", error);
      addToast("Error", "Failed to reject dish.", "error");
    } finally {
      setRejectInProgress(false);
    }
  };

  const handleApproveAddon = async (dish, addon) => {
    try {
      await api.post("/restaurant-management/menu/approve-addon", {
        dishId: dish._id,
        addonId: addon._id,
        markup: addon.markup || 0,
      });
      await fetchDishes();
      addToast("Add-on Approved", "Add-on has been approved.", "success");
    } catch (error) {
      console.error("Error approving add-on:", error);
      addToast("Error", "Failed to approve add-on.", "error");
    }
  };

  const saveAddonDetails = async (shouldApprove = false) => {
    if (!selectedAddonDish || !selectedAddon || !validateAddonForm()) {
      return;
    }

    setAddonSubmitInProgress(true);
    const markup = toNumber(addonForm.markup);

    try {
      await api.post("/restaurant-management/menu/update-addon-details", {
        dishId: selectedAddonDish._id,
        addonId: selectedAddon._id,
        name: addonForm.name.trim(),
        dishType: addonForm.dishType,
        itemPrice: toNumber(addonForm.itemPrice),
        markup,
      });

      if (shouldApprove) {
        await api.post("/restaurant-management/menu/approve-addon", {
          dishId: selectedAddonDish._id,
          addonId: selectedAddon._id,
          markup,
        });
      }

      await fetchDishes();
      addToast(
        shouldApprove ? "Add-on Approved" : "Add-on Updated",
        shouldApprove
          ? "Add-on has been updated and approved."
          : "Add-on details have been updated.",
        "success"
      );
      resetAddonModalState();
    } catch (error) {
      console.error("Error saving add-on details:", error);
      addToast("Error", "Failed to save add-on details.", "error");
    } finally {
      setAddonSubmitInProgress(false);
    }
  };

  const handleRejectAddon = async (dish, addon) => {
    try {
      await api.post("/restaurant-management/menu/reject-addon", {
        dishId: dish._id,
        addonId: addon._id,
      });
      await fetchDishes();
      addToast("Add-on Rejected", "Add-on has been rejected.", "success");
    } catch (error) {
      console.error("Error rejecting add-on:", error);
      addToast("Error", "Failed to reject add-on.", "error");
    }
  };

  return (
    <div className="menu-verification-container">
      <div className="menu-verification-header">
        <button className="back-btn" onClick={onBack}>
          {"<- Back"}
        </button>
        <h2>Menu Verification</h2>
      </div>

      <div className="verification-tabs">
        <button
          className={`tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Pending
        </button>
        <button
          className={`tab ${activeTab === "verified" ? "active" : ""}`}
          onClick={() => setActiveTab("verified")}
        >
          Verified
        </button>
      </div>

      <div className="menu-verification-table-wrap">
        {loading ? (
          <div className="menu-verification-empty">Loading dishes...</div>
        ) : dishes.length === 0 ? (
          <div className="menu-verification-empty">
            No {activeTab} dishes found
          </div>
        ) : (
          <table className="menu-verification-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Item Price</th>
                <th>Markup</th>
                <th>Total Price</th>
                <th>Category</th>
                <th>Type</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dishes.map((dish) => (
                <tr key={dish._id}>
                  <td>
                    <div className="dish-thumb">
                      {dish.dishImageUrl?.[0] ? (
                        <img src={dish.dishImageUrl[0]} alt={dish.dishName} />
                      ) : (
                        <span>No Image</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="dish-name-cell">
                      <strong>{dish.dishName}</strong>
                      <span>{dish.dishDescription || "No description"}</span>
                      {dish.addons?.length > 0 ? (
                        <div className="verification-addon-list">
                          {dish.addons.map((addon) => (
                            <div key={addon._id} className="verification-addon-item">
                              <span>{addon.name}</span>
                              <span>Rs. {addon.itemPrice}</span>
                              <span>Markup Rs. {addon.markup || 0}</span>
                              <span>{addon.status === "verified" ? "Verified" : addon.status.replace("_", " ")}</span>
                              <span className="verification-addon-actions">
                                <button onClick={() => openAddonModal(dish, addon)}>
                                  Edit
                                </button>
                                {addon.status === "pending_verification" ? (
                                  <>
                                  <button onClick={() => handleApproveAddon(dish, addon)}>
                                    Verify
                                  </button>
                                  <button
                                    className="reject-addon-button"
                                    onClick={() => handleRejectAddon(dish, addon)}
                                  >
                                    Reject
                                  </button>
                                  </>
                                ) : null}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td>Rs. {dish.itemPrice}</td>
                  <td>Rs. {dish.markup || 0}</td>
                  <td>Rs. {dish.dishPrice}</td>
                  <td>{dish.dishCategory || "N/A"}</td>
                  <td>{dish.dishType === 0 ? "Veg" : "Non-Veg"}</td>
                  <td>{formatDate(dish.createdAt)}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="secondary-btn"
                        onClick={() => openDishModal(dish, "edit")}
                      >
                        Edit
                      </button>
                      {activeTab === "pending" ? (
                        <>
                          <button
                            className="approve-btn"
                            onClick={() => openDishModal(dish, "approve")}
                          >
                            Approve
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleRejectDish(dish)}
                            disabled={rejectInProgress}
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="verified-badge">Verified</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && selectedDish && (
        <div className="menu-verification-modal-overlay">
          <div className="menu-verification-modal-content">
            <div className="menu-verification-modal-header">
              <h3>
                {modalAction === "approve" ? "Approve Dish" : "Edit Dish"}
              </h3>
              <button className="close-button" onClick={closeModal}>
                <img src={cross} alt="close" />
              </button>
            </div>

            <div className="menu-verification-modal-body">
              <div className="input-group">
                <label>Dish Name</label>
                <input
                  type="text"
                  value={form.dishName}
                  onChange={(event) =>
                    updateFormValue("dishName", event.target.value)
                  }
                />
                {errors.dishName ? (
                  <span className="error-message">{errors.dishName}</span>
                ) : null}
              </div>

              <div className="input-group">
                <label>Choose Category</label>
                <select
                  value={form.category}
                  onChange={(event) =>
                    updateFormValue("category", event.target.value)
                  }
                >
                  <option value="" disabled>
                    Select category
                  </option>
                  {availableCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                  {/* <option value="Others">Others</option> */}
                </select>
                {errors.category ? (
                  <span className="error-message">{errors.category}</span>
                ) : null}
              </div>

              {form.category === "Others" ? (
                <div className="input-group">
                  <label>New Category</label>
                  <input
                    type="text"
                    value={form.addNewCategory}
                    onChange={(event) =>
                      updateFormValue("addNewCategory", event.target.value)
                    }
                  />
                  {errors.addNewCategory ? (
                    <span className="error-message">
                      {errors.addNewCategory}
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div className="veg-nonveg-toggle">
                <button
                  type="button"
                  className={form.isVeg ? "toggle-button active" : "toggle-button"}
                  onClick={() => updateFormValue("isVeg", true)}
                >
                  <img src={Veg} alt="veg" />
                  Veg
                </button>
                <button
                  type="button"
                  className={!form.isVeg ? "toggle-button active" : "toggle-button"}
                  onClick={() => updateFormValue("isVeg", false)}
                >
                  <img src={NonVeg} alt="non veg" />
                  Non-Veg
                </button>
              </div>

              <div className="input-group">
                <label>Description</label>
                <textarea
                  value={form.dishDescription}
                  onChange={(event) =>
                    updateFormValue("dishDescription", event.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="input-group">
                <label>Dish Image</label>
                <div className="image-preview-container">
                  {form.images.map((image, index) => (
                    <div key={`${image.key || image.preview}-${index}`} className="image-preview">
                      <img src={image.preview} alt={`Dish ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-image-button"
                        onClick={() => handleRemoveImage(index)}
                      >
                        x
                      </button>
                    </div>
                  ))}
                  {form.images.length < 1 ? (
                    <button
                      type="button"
                      className="add-image"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      +
                    </button>
                  ) : null}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                {errors.images ? (
                  <span className="error-message">{errors.images}</span>
                ) : null}
              </div>

              <div className="price-grid">
                <div className="input-group">
                  <label>Item Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.itemPrice}
                    onChange={(event) =>
                      updateFormValue("itemPrice", event.target.value)
                    }
                  />
                  {errors.itemPrice ? (
                    <span className="error-message">{errors.itemPrice}</span>
                  ) : null}
                </div>

                <div className="input-group">
                  <label>Markup</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.markup}
                    onChange={(event) =>
                      updateFormValue("markup", event.target.value)
                    }
                  />
                  {errors.markup ? (
                    <span className="error-message">{errors.markup}</span>
                  ) : null}
                </div>

                <div className="input-group">
                  <label>Total Price</label>
                  <input type="text" value={totalPrice} disabled />
                </div>
              </div>
            </div>

            <div className="menu-verification-modal-footer">
              <button className="secondary-btn" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="secondary-btn"
                onClick={() => saveDishDetails(false)}
                disabled={submitInProgress}
              >
                {submitInProgress ? "Saving..." : "Save Changes"}
              </button>
              {activeTab === "pending" ? (
                <button
                  className="approve-btn"
                  onClick={() => saveDishDetails(true)}
                  disabled={submitInProgress}
                >
                  {submitInProgress ? "Processing..." : "Approve Dish"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {isAddonModalOpen && selectedAddon && (
        <div className="menu-verification-modal-overlay">
          <div className="menu-verification-modal-content addon-modal-content">
            <div className="menu-verification-modal-header">
              <h3>Edit Add-on</h3>
              <button className="close-button" onClick={resetAddonModalState}>
                <img src={cross} alt="close" />
              </button>
            </div>

            <div className="menu-verification-modal-body addon-modal-body">
              <div className="input-group">
                <label>Add-on Name</label>
                <input
                  type="text"
                  value={addonForm.name}
                  onChange={(event) =>
                    updateAddonFormValue("name", event.target.value)
                  }
                />
                {addonErrors.name ? (
                  <span className="error-message">{addonErrors.name}</span>
                ) : null}
              </div>

              <div className="veg-nonveg-toggle">
                <button
                  type="button"
                  className={addonForm.dishType === 0 ? "toggle-button active" : "toggle-button"}
                  onClick={() => updateAddonFormValue("dishType", 0)}
                >
                  <img src={Veg} alt="veg" />
                  Veg
                </button>
                <button
                  type="button"
                  className={addonForm.dishType === 1 ? "toggle-button active" : "toggle-button"}
                  onClick={() => updateAddonFormValue("dishType", 1)}
                >
                  <img src={NonVeg} alt="non veg" />
                  Non-Veg
                </button>
              </div>

              <div className="price-grid">
                <div className="input-group">
                  <label>Item Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={addonForm.itemPrice}
                    onChange={(event) =>
                      updateAddonFormValue("itemPrice", event.target.value)
                    }
                  />
                  {addonErrors.itemPrice ? (
                    <span className="error-message">{addonErrors.itemPrice}</span>
                  ) : null}
                </div>

                <div className="input-group">
                  <label>Markup</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={addonForm.markup}
                    onChange={(event) =>
                      updateAddonFormValue("markup", event.target.value)
                    }
                  />
                  {addonErrors.markup ? (
                    <span className="error-message">{addonErrors.markup}</span>
                  ) : null}
                </div>

                <div className="input-group">
                  <label>Total Price</label>
                  <input type="text" value={addonTotalPrice} disabled />
                </div>
              </div>

              <div className="input-group">
                <label>Status</label>
                <input
                  type="text"
                  value={String(selectedAddon.status || "pending_verification").replace("_", " ")}
                  disabled
                />
              </div>
            </div>

            <div className="menu-verification-modal-footer">
              <button className="secondary-btn" onClick={resetAddonModalState}>
                Cancel
              </button>
              <button
                className="secondary-btn"
                onClick={() => saveAddonDetails(false)}
                disabled={addonSubmitInProgress}
              >
                {addonSubmitInProgress ? "Saving..." : "Save Changes"}
              </button>
              {selectedAddon.status === "pending_verification" ? (
                <button
                  className="approve-btn"
                  onClick={() => saveAddonDetails(true)}
                  disabled={addonSubmitInProgress}
                >
                  {addonSubmitInProgress ? "Processing..." : "Verify Add-on"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuVerification;
